import { HTMLAttributes, ReactNode } from 'react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { type AdvancedFormReturnType } from 'helpers/form/useAdvancedForm';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import { Field } from 'components/uikit/Field';
import { HookFormSwitch } from 'components/uikit/switch/HookFormSwitch.tsx';
import { IconPicker } from 'components/uikit/inputs/IconPicker';
import { DropDownInput } from 'components/uikit/inputs/dropdown/DropDownInput.tsx';
import { Input } from 'components/uikit/inputs/Input';
import {
  CreateProjectDto,
  ProjectTeamMemberRole,
  SearchTeamMemberResultDto,
  TeamMemberDto,
} from 'services/api/api-client';
import { TeamMemberItem } from './TeamMemberItem.tsx';
import styles from './ProjectModal.module.scss';
import { useTeamMemberActions } from './useTeamMemberActions.ts';
import { Box } from '@mui/material';

type ProjectMainTabProps = {
  form: AdvancedFormReturnType<CreateProjectDto>;
  projectIconCode?: string;
  isPrivate: boolean;
  availableTeamMembers: SearchTeamMemberResultDto[];
  teamMembers: TeamMemberDto[];
  currentUserId: string | null;
  setTeamMembers: React.Dispatch<React.SetStateAction<TeamMemberDto[]>>;
};

export const ProjectMainTab: React.FC<ProjectMainTabProps> = ({
  form,
  projectIconCode,
  isPrivate,
  availableTeamMembers,
  teamMembers,
  currentUserId,
  setTeamMembers,
}) => {
  const i18n = useScopedTranslation('Component.ProjectModal');

  const renderTeamMemberOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: SearchTeamMemberResultDto,
  ): ReactNode => {
    if (!option) return <></>;

    return (
      <Box
        {...props}
        component="li"
        key={`option-${option.id}`}
        className={styles.dropdownOption}
      >
        <span className={styles.dropdownOptionLabel}>
          <div className={styles.teamMemberOptionName}>{option.fullName}</div>
          <div className={styles.teamMemberOptionEmail}>{option.email}</div>
        </span>
      </Box>
    );
  };

  const { onDelete, onChangeResponsible, onChangeRole, onAdd } =
    useTeamMemberActions({
      setTeamMembers,
    });

  return (
    <>
      <Field title={i18n.t('name_field')} className={styles.inputField}>
        <Input
          key={'name'}
          {...form.register('name', { ...requiredRule() })}
          errorText={form.formState.errors.name?.message}
          maxLength={70}
        />
      </Field>
      <Field title={i18n.t('description_field')} className={styles.inputField}>
        <Input
          {...form.register('description')}
          errorText={form.formState.errors.description?.message}
        />
      </Field>
      <Field title={i18n.t('icon_field')} className={styles.inputField}>
        <IconPicker
          name="iconCode"
          form={form}
          controlName="iconCode"
          defaultIconCode={projectIconCode ?? ''}
        />
      </Field>
      <div className={styles.privateSwitch}>
        <HookFormSwitch<CreateProjectDto>
          control={form.control}
          name="isPrivate"
        />
        <div className={styles.privateTitle}>{i18n.t('private_project')}</div>
      </div>
      {isPrivate && availableTeamMembers && (
        <Field title={i18n.t('team_field')} className={styles.inputField}>
          <DropDownInput
            onValueChanged={async (newSelectedOption, _) => {
              if (!newSelectedOption) return;
              onAdd(newSelectedOption);
            }}
            enableSearch={true}
            options={availableTeamMembers}
            value={null}
            getOptionLabel={(option: SearchTeamMemberResultDto) =>
              option.fullName
            }
            renderOption={renderTeamMemberOption}
            renderInput={(params) => <Input {...params} />}
            placeholder={''}
          />
          <div className={styles.teamMembers}>
            {teamMembers.map((tm) => (
              <TeamMemberItem
                key={tm.id}
                teamMember={tm}
                currentUserId={currentUserId}
                onDelete={() => onDelete(tm)}
                onChangeResponsible={() => onChangeResponsible(tm)}
                onChangeRole={(role: ProjectTeamMemberRole) =>
                  onChangeRole(role, tm)
                }
              />
            ))}
          </div>
        </Field>
      )}
    </>
  );
};
